import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Plus, Edit, Trash2, Globe, Store, Search, Building2, Mail, Phone, Landmark, CreditCard, Hash } from 'lucide-react';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = { nombre: '', nit: '', email: '', banco: '', numero_cuenta: '', tipo_cuenta: '', telefono: '', es_global: true, tiendas_ids: [] };

function Field({ label, hint, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      {children}
      {hint && <p className="text-[11px] text-slate-500 dark:text-slate-400">{hint}</p>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-4">
      <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#2c57c5] dark:text-blue-300">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </section>
  );
}

function Info({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 min-w-0 text-sm text-slate-600 dark:text-slate-400">
      <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      <span className="truncate">{children}</span>
    </div>
  );
}

function ProviderCard({ provider, getStoreName, onEdit, onDelete }) {
  const global = provider?.es_global !== false;
  return (
    <Card className="p-4 sm:p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-[#2c57c5]/40 transition-colors" data-testid={`provider-card-${provider.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-base sm:text-lg break-words">{provider?.nombre || 'Sin nombre'}</h3>
            <Badge className={global ? 'bg-blue-500 text-white' : 'bg-purple-500 text-white'}>
              {global ? <Globe className="h-3 w-3 mr-1" /> : <Store className="h-3 w-3 mr-1" />}
              {global ? 'Global' : 'Específico'}
            </Badge>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 mt-2">
            <Info icon={Hash}>NIT: {provider?.nit || 'N/A'}</Info>
            {provider?.email && <Info icon={Mail}>{provider.email}</Info>}
            {provider?.telefono && <Info icon={Phone}>{provider.telefono}</Info>}
            {provider?.banco && <Info icon={Landmark}>{provider.banco}</Info>}
            {provider?.numero_cuenta && <Info icon={CreditCard}>{provider.numero_cuenta}{provider?.tipo_cuenta ? ` · ${provider.tipo_cuenta}` : ''}</Info>}
          </div>
          {!global && provider?.tiendas_ids?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {provider.tiendas_ids.map(sid => (
                <Badge key={sid} variant="outline" className="text-xs dark:border-slate-700 dark:text-slate-300">{getStoreName(sid)}</Badge>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => onEdit(provider)} data-testid={`edit-provider-${provider.id}`}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => onDelete(provider.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-provider-${provider.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default function AdminProviders() {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [stores, setStores] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    fetchProviders();
    axios.get(`${API}/stores`).then(r => setStores(r.data || [])).catch(() => {});
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await axios.get(`${API}/providers`);
      setProviders(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Error al cargar proveedores');
      setProviders([]);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return providers;
    return providers.filter(p => `${p.nombre || ''} ${p.nit || ''} ${p.banco || ''}`.toLowerCase().includes(q));
  }, [providers, search]);

  const set = (key) => (e) => setFormData(prev => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const dataToSend = { ...formData, tiendas_ids: formData.es_global ? [] : formData.tiendas_ids };
      if (editingProvider) {
        await axios.put(`${API}/admin/providers/${editingProvider.id}`, dataToSend);
        toast.success('Proveedor actualizado');
      } else {
        await axios.post(`${API}/admin/providers`, dataToSend);
        toast.success('Proveedor creado');
      }
      setShowDialog(false);
      setEditingProvider(null);
      setFormData(EMPTY_FORM);
      await fetchProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar proveedor');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (provider) => {
    setEditingProvider(provider);
    setFormData({
      nombre: provider.nombre,
      nit: provider.nit,
      email: provider.email || '',
      banco: provider.banco || '',
      numero_cuenta: provider.numero_cuenta || '',
      tipo_cuenta: provider.tipo_cuenta || '',
      telefono: provider.telefono || '',
      es_global: provider.es_global !== false,
      tiendas_ids: provider.tiendas_ids || []
    });
    setShowDialog(true);
  };

  const handleDelete = async (providerId) => {
    if (!window.confirm('¿Estás seguro de eliminar este proveedor?')) return;
    try {
      await axios.delete(`${API}/admin/providers/${providerId}`);
      toast.success('Proveedor eliminado');
      fetchProviders();
    } catch (error) {
      toast.error('Error al eliminar proveedor');
    }
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setTimeout(() => { setEditingProvider(null); setFormData(EMPTY_FORM); }, 300);
  };

  const handleStoreToggle = (storeId) => {
    setFormData(prev => ({
      ...prev,
      tiendas_ids: prev.tiendas_ids.includes(storeId)
        ? prev.tiendas_ids.filter(id => id !== storeId)
        : [...prev.tiendas_ids, storeId]
    }));
  };

  const getStoreName = (storeId) => stores.find(s => s.id === storeId)?.nombre || storeId;
  const invalidStores = !formData.es_global && formData.tiendas_ids.length === 0;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="admin-providers-page">
      <div className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="container mx-auto px-4 pt-3 pb-5">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="-ml-2 text-slate-600 dark:text-slate-300" data-testid="back-to-dashboard-btn">
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver al Dashboard
          </Button>
          <div className="mt-2 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Building2 className="h-6 w-6 text-[#2c57c5]" /> Gestión de Proveedores
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1" data-testid="providers-count">{providers.length} proveedores registrados</p>
            </div>
            <Button onClick={() => setShowDialog(true)} className="bg-[#2c57c5] hover:bg-[#1e3a8a] text-white w-full sm:w-auto h-11" data-testid="new-provider-btn">
              <Plus className="mr-2 h-4 w-4" /> Nuevo Proveedor
            </Button>
          </div>
          <div className="relative mt-4">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre, NIT o banco..." className="pl-9 h-11" data-testid="providers-search" />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {filtered.length === 0 ? (
          <Card className="p-10 text-center bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" data-testid="providers-empty">
            <p className="text-slate-500 dark:text-slate-400">No se encontraron proveedores</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="providers-list">
            {filtered.map(p => (
              <ProviderCard key={p.id} provider={p} getStoreName={getStoreName} onEdit={handleEdit} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={(v) => { if (!v) handleCloseDialog(); }}>
        <DialogContent
          className="p-0 gap-0 flex flex-col w-full max-w-none h-[100dvh] max-h-[100dvh] rounded-none sm:max-w-3xl sm:h-auto sm:max-h-[90vh] sm:rounded-xl dark:bg-slate-900 dark:border-slate-800"
          data-testid="provider-dialog"
        >
          <DialogHeader className="px-5 sm:px-6 pt-5 pb-4 border-b border-slate-200 dark:border-slate-800 text-left">
            <DialogTitle className="text-lg sm:text-xl">{editingProvider ? 'Editar Proveedor' : 'Crear Nuevo Proveedor'}</DialogTitle>
            <DialogDescription>Los campos con * son obligatorios.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 sm:px-6 py-5 space-y-7">
              <Section title="Datos generales">
                <Field label="Nombre del proveedor" required>
                  <Input className="h-11" value={formData.nombre} onChange={set('nombre')} required autoFocus data-testid="provider-nombre" />
                </Field>
                <Field label="NIT" required>
                  <Input className="h-11" value={formData.nit} onChange={set('nit')} required data-testid="provider-nit" />
                </Field>
              </Section>
              <Section title="Contacto">
                <Field label="Email" hint="Para recibir notificaciones de pago y boletas">
                  <Input className="h-11" type="email" inputMode="email" value={formData.email} onChange={set('email')} placeholder="correo@proveedor.com" data-testid="provider-email" />
                </Field>
                <Field label="Teléfono WhatsApp" hint="Para enviar boletas automáticamente">
                  <Input className="h-11" type="tel" inputMode="tel" value={formData.telefono} onChange={set('telefono')} placeholder="+502 1234 5678" data-testid="provider-telefono" />
                </Field>
              </Section>
              <Section title="Datos bancarios">
                <Field label="Banco">
                  <Input className="h-11" value={formData.banco} onChange={set('banco')} data-testid="provider-banco" />
                </Field>
                <Field label="Número de cuenta">
                  <Input className="h-11" inputMode="numeric" value={formData.numero_cuenta} onChange={set('numero_cuenta')} data-testid="provider-cuenta" />
                </Field>
                <Field label="Tipo de cuenta">
                  <Input className="h-11" value={formData.tipo_cuenta} onChange={set('tipo_cuenta')} placeholder="Ej: Ahorro, Monetaria" data-testid="provider-tipo-cuenta" />
                </Field>
              </Section>

              <section className="space-y-3 p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40">
                <label htmlFor="es_global" className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    id="es_global"
                    checked={formData.es_global}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, es_global: !!checked }))}
                    className="mt-0.5"
                    data-testid="provider-es-global"
                  />
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200 flex items-center">
                    <Globe className="h-4 w-4 mr-2 text-blue-500 shrink-0" />
                    Proveedor global (visible en todas las tiendas)
                  </span>
                </label>
                {!formData.es_global && (
                  <div className="pt-1">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center">
                      <Store className="h-4 w-4 mr-2 text-purple-500" /> Seleccionar tiendas específicas
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-56 overflow-y-auto">
                      {stores.map(store => (
                        <label key={store.id} htmlFor={`store-${store.id}`} className="flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 cursor-pointer hover:border-purple-400 transition-colors">
                          <Checkbox id={`store-${store.id}`} checked={formData.tiendas_ids.includes(store.id)} onCheckedChange={() => handleStoreToggle(store.id)} data-testid={`provider-store-${store.id}`} />
                          <span className="text-sm text-slate-700 dark:text-slate-300 truncate">{store.nombre}</span>
                        </label>
                      ))}
                    </div>
                    {invalidStores && <p className="text-xs text-amber-600 mt-2">Seleccione al menos una tienda</p>}
                  </div>
                )}
              </section>
            </div>
            <div className="px-5 sm:px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="h-11" data-testid="provider-cancel-btn">Cancelar</Button>
              <Button type="submit" disabled={loading || invalidStores} className="bg-[#2c57c5] hover:bg-[#1e3a8a] text-white h-11 sm:min-w-[140px]" data-testid="provider-save-btn">
                {loading ? 'Guardando...' : (editingProvider ? 'Actualizar' : 'Crear')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
